import Cookies from 'js-cookie';

import LogHelperFactory from './logging';

let mockstate;
let respType = new URL(window.location.href).searchParams.get('resp_type');
respType = respType === null ? undefined : parseInt(respType, 10);

const injectMockStates = (url) => {
  mockstate = new URL(window.location.href).searchParams.get('mockstate');
  let mockUrl = url;
  if (mockstate) {
    let param = `mockstate=${encodeURI(mockstate)}`;
    if (respType !== undefined) {
      param = `${param}&resp_type=${respType}`;
    }
    if (url.indexOf('?') !== -1) {
      param = `&${param}`;
    } else {
      param = `?${param}`;
    }
    mockUrl += param;
  }
  return mockUrl;
};

class HttpError extends Error {
  constructor(status, data) {
    super(`HTTP ${status}`);
    this.status = status;
    this.data = data;
  }
}

async function getJSON(response) {
  const { status } = response;
  if (status === 204) return {};
  const responseForError = response.clone();
  try {
    const json = await response.json();
    return json;
  } catch (error) {
    const body = await responseForError.text();
    throw new HttpError(status, { body });
  }
}

let inFlight = 0;
const scheduleAdvanceMock = (after) => {
  inFlight += 1;
  after.finally((result) => {
    inFlight -= 1;
    if (inFlight === 0) {
      respType += 1;
    }
  });
};

const ClientFactory = (factoryOptions = {}) => {
  const defaultOptions = { logger: console, basePath: '' };
  const {
    logger,
    kibanaServiceLog,
    basePath,
    onError,
  } = {
    ...defaultOptions,
    ...factoryOptions,
  };
  const defaultLogData = {
    segment: Cookies.get('xf_seg') || '',
    tid: Cookies.get('xf_tid') || '',
  };
  const log = (name, data, ...args) => {
    logger.log({ ...defaultLogData, ...data }, ...args);
    if (kibanaServiceLog !== undefined) {
      kibanaServiceLog({ event: name, ...data });
    }
  };
  const logRequest = LogHelperFactory(log);

  if (logger === console) {
    // eslint-disable-next-line no-console
    console.warn('No logger passed in maw-fetch client configuration.');
  }

  const defaultHeaders = {};
  const setHeaders = (headers) => {
    Object.assign(defaultHeaders, headers);
    return { ...defaultHeaders };
  };

  const requestFetch = (url, options = {}) => {
    const {
      headers,
      ...requestOptions
    } = options;

    // prepend basePath ONLY if url path is relative
    const normalizedUrl = /^\//.test(url) ? `${basePath}${url}` : url;

    const modifiedUrl = injectMockStates(normalizedUrl);
    const modifiedOptions = {
      method: 'get',
      ...requestOptions,
      credentials: 'same-origin',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json; v=2',
        'X-XSRF-TOKEN': Cookies.get('XSRF-TOKEN'),
        'COTTON-XSRF-TOKEN': Cookies.get('COTTON-XSRF-TOKEN'),
        ...defaultHeaders,
        ...headers,
      }),
    };

    const requestObj = {
      url: modifiedUrl,
      options: modifiedOptions,
      page: window.location.pathname,
    };

    const tryFetch = async (request) => {
      const { logResponse, logError } = logRequest(request);
      try {
        const response = await fetch(request.url, request.options);
        const { ok, status } = response;
        const json = getJSON(response);
        if (mockstate && respType !== undefined) {
          const method = modifiedOptions.method.toLowerCase();
          if (method === 'post' || method === 'put' || method === 'delete') {
            scheduleAdvanceMock(json);
          }
        }
        const data = await json;
        const responseObj = { data, status };
        if (ok && status < 400) {
          logResponse(responseObj, request);
          return data;
        }
        throw responseObj;
      } catch (error) {
        logError(error, request);
        if (typeof onError === 'function') {
          return onError({
            request,
            error,
            logger,
            basePath,
            retry: tryFetch,
          });
        }

        throw error;
      }
    };

    return tryFetch(requestObj);
  };

  return {
    fetch: requestFetch,
    setHeaders,
  };
};

export default ClientFactory;
